import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { authApi } from '../../api/auth.api';
import { startSession } from '../../model/session';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { Input } from '@/shared/ui/atoms/Input';
import { FontIcon } from '@/shared/ui/atoms/FontIcon';
import { PasswordInput } from '@/shared/ui/molecules/PasswordInput';
import { Spinner } from '@/shared/ui/atoms/Spinner';
import { FormField } from '@/shared/ui/molecules/FormField';
import '../AuthForm.styl';

const PASSWORD_MIN = 10;

type Props = { token: string };

export function InvitationForm({ token }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'auth.invitation' });
  const { t: tAuth } = useTranslation(undefined, { keyPrefix: 'auth' });
  const { t: tRoot } = useTranslation();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invitation = useQuery({ queryKey: ['invitation', token], queryFn: () => authApi.invitation(token), retry: false });

  const accept = useMutation({
    mutationFn: () => authApi.acceptInvitation(token, displayName.trim(), password),
    onSuccess: (res) => navigate(startSession(res), { replace: true }),
    onError: (err) => setError(errorMessage(err, t('invalid')))
  });

  if (invitation.isPending) return <Spinner label={t('loading')} />;
  if (invitation.isError || !invitation.data) {
    return (
      <div className="f-auth">
        <h1 className="f-auth__title">{t('tokenTitle')}</h1>
        <div className="f-auth__alert" role="alert">
          {t('invalid')}
        </div>
        <div className="f-auth__foot">
          <Link className="f-auth__link" to="/login">
            {tAuth('forgot.back')}
          </Link>
        </div>
      </div>
    );
  }

  const inv = invitation.data;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (displayName.trim().length < 2) return setError(t('nameRequired'));
    if (password.length < PASSWORD_MIN) return setError(tAuth('reset.tooShort'));
    accept.mutate();
  };

  return (
    <form className="f-auth" onSubmit={submit} noValidate>
      <p className="f-auth__lead">{t('title')}</p>
      <div className="f-auth__tenant">{inv.tenantName}</div>
      <dl className="f-auth__facts">
        <dt>{t('email')}</dt>
        <dd>{inv.email}</dd>
        <dt>{t('role')}</dt>
        <dd>{tRoot(`roles.${inv.role}`)}</dd>
      </dl>
      <div className="f-auth__fields">
        <FormField label={t('name')} htmlFor="inv-name">
          <Input id="inv-name" autoComplete="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} leading={<FontIcon name="user4" />} />
        </FormField>
        <FormField label={t('password')} htmlFor="inv-pwd" hint={tAuth('reset.lead')}>
          <PasswordInput id="inv-pwd" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </FormField>
      </div>
      {error ? (
        <div className="f-auth__alert" role="alert">
          {error}
        </div>
      ) : null}
      <Button type="submit" block className="f-auth__submit" loading={accept.isPending}>
        {t('submit')}
      </Button>
    </form>
  );
}
